import {
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { startWith } from 'rxjs/operators';
import { DateTimeUtility } from '../utility/date-time.utility';
import { EmailListValidator } from '../validators/email-list.validator';
import { BaseResponse } from './base-response.model';
import { Validation } from './validation.model';

export interface ReportSchedulesResonse extends BaseResponse {
	message: string;
	schedules: ReportScheduleJSON[];
}

export interface ReportScheduleResonse extends BaseResponse {
	message: string;
	schedule: ReportScheduleJSON;
}

export interface ReportScheduleJSON {
	id: number;
	title: string;
	reportId: number;
	reportName: string;
	createdBy: string;
	active: boolean;
	untilChecked: boolean;
	untilDate: string;
	scheduleDate: string;
	repeating: boolean;
	repeatEvery: string;
	reportFormat: string;
	emailReport: boolean;
	recipients: string;
	subject: string;
	message: string;
	updatedBy: string;
	userId: number;
	lastUpdated: string;
}

export interface SaveReportSchedule {
	id: number;
	title: string;
	reportId: number;
	active: boolean;
	reportFormat: string;
	scheduleDate: Date;
	repeating: boolean;
	repeatEvery: string;
	untilChecked: boolean;
	untilDate: Date;
	emailReport: boolean;
	recipients: string;
	subject: string;
	message: string;
}

export class ReportSchedule {
	constructor(
		public id: number,
		public title: string,
		public reportId: number,
		public reportName: string,
		public createdBy: string,
		public active: boolean,
		public untilChecked: boolean,
		public untilDate: Date,
		public scheduleDate: Date,
		public repeating: boolean,
		public repeatEvery: string,
		public reportFormat: string,
		public emailReport: boolean,
		public recipients: string,
		public subject: string,
		public message: string,
		public updatedBy: string,
		public userId: number,
		public lastUpdated: Date
	) {}

	static fromJSON(json: ReportScheduleJSON): ReportSchedule {
		return Object.assign(Object.create(ReportSchedule.prototype), json, {
			lastUpdated: json.lastUpdated
				? DateTimeUtility.fromDatabaseDatetime(json.lastUpdated)
				: null,
			scheduleDate: json.scheduleDate
				? DateTimeUtility.fromDatabaseDate(json.scheduleDate)
				: null,
			untilDate: json.untilDate
				? DateTimeUtility.fromDatabaseDate(json.untilDate)
				: null,
		});
	}

	static createFormGroup(
		s: ReportSchedule,
		validation: Validation
	): UntypedFormGroup {
		const fg: UntypedFormGroup = new UntypedFormGroup({
			id: new UntypedFormControl(s ? s.id : null),
			title: new UntypedFormControl(s ? s.title : null, [
				Validators.required,
				Validators.maxLength(validation.name.maxLength),
				Validators.pattern(validation.name.pattern),
			]),
			reportId: new UntypedFormControl(s ? s.reportId : null, [
				Validators.required,
			]),
			active: new UntypedFormControl(s ? s.active : true),
			reportFormat: new UntypedFormControl(s ? s.reportFormat : null, [
				Validators.required,
			]),
			scheduleDate: new UntypedFormControl(s ? s.scheduleDate : null, [
				Validators.required,
			]),
			repeating: new UntypedFormControl(s ? s.repeating : false),
			repeatEvery: new UntypedFormControl(s ? s.repeatEvery : null, [
				Validators.required,
			]),
			untilChecked: new UntypedFormControl(s ? s.untilChecked : false),
			untilDate: new UntypedFormControl(s ? s.untilDate : null, [
				Validators.required,
			]),
			emailReport: new UntypedFormControl(s ? s.emailReport : false),
			recipients: new UntypedFormControl(s ? s.recipients : null, [
				Validators.required,
				EmailListValidator(
					validation.email.maxLength,
					validation.email.pattern
				),
			]),
			subject: new UntypedFormControl(s ? s.subject : null, [
				Validators.required,
				Validators.maxLength(validation.name.maxLength),
				Validators.pattern(validation.name.pattern),
			]),
			message: new UntypedFormControl(s ? s.message : null, [
				Validators.required,
				Validators.maxLength(validation.comment.maxLength),
				Validators.pattern(validation.comment.pattern),
			]),
		});
		fg.get('untilChecked')
			.valueChanges.pipe(startWith(fg.get('untilChecked').value))
			.subscribe((value) => {
				if (value) {
					fg.get('untilDate').enable();
				} else {
					fg.get('untilDate').disable();
				}
			});
		fg.get('repeating')
			.valueChanges.pipe(startWith(fg.get('repeating').value))
			.subscribe((value) => {
				if (value) {
					fg.get('repeatEvery').enable();
					fg.get('untilChecked').enable();
				} else {
					fg.get('repeatEvery').disable();
					fg.get('untilChecked').setValue(false);
					fg.get('untilChecked').disable();
				}
			});
		fg.get('emailReport')
			.valueChanges.pipe(startWith(fg.get('emailReport').value))
			.subscribe((value) => {
				if (value) {
					fg.get('recipients').enable();
					fg.get('subject').enable();
					fg.get('message').enable();
				} else {
					fg.get('recipients').disable();
					fg.get('subject').disable();
					fg.get('message').disable();
				}
			});
		return fg;
	}

	static fromFormGroup(fg: UntypedFormGroup): ReportSchedule {
		return Object.assign(Object.create(ReportSchedule.prototype), {
			id: fg.get('id').value,
			title: fg.get('title').value,
			reportId: fg.get('reportId').value,
			active: fg.get('active').value,
			reportFormat: fg.get('reportFormat').value,
			scheduleDate: DateTimeUtility.toDatabaseDateString(
				fg.get('scheduleDate').value
			),
			repeating: fg.get('repeating').value,
			repeatEvery: fg.get('repeatEvery').value,
			untilChecked: fg.get('untilChecked').value,
			untilDate: DateTimeUtility.toDatabaseDateString(
				fg.get('untilDate').value
			),
			emailReport: fg.get('emailReport').value,
			recipients: fg.get('recipients').value,
			subject: fg.get('subject').value,
			message: fg.get('message').value,
		});
	}

	toSaveReportSchedule(): SaveReportSchedule {
		return {
			id: this.id,
			title: this.title,
			reportId: this.reportId,
			active: this.active,
			reportFormat: this.reportFormat,
			scheduleDate: this.scheduleDate,
			repeating: this.repeating,
			repeatEvery: this.repeatEvery,
			untilChecked: this.untilChecked,
			untilDate: this.untilDate,
			emailReport: this.emailReport,
			recipients: this.recipients,
			subject: this.subject,
			message: this.message,
		};
	}
}
